import React from 'react';
import { FormattedMessage } from 'react-intl';

import { Divider } from 'ui-components/components/divider';
import { FlexColumn, Gap } from 'ui-components/components/flex';
import { RadioCheckList } from 'ui-components/components/radio-check-list';
import { RadioCheckListItem } from 'ui-components/components/radio-check-list/radio-check-list-item';
import { Text, TextType } from 'ui-components/components/text';

import { FbsApplicationDrawerSection } from '../fbs-application-drawer-section';
import { messages } from './messages';
import styles from './shipment-volume-timeline-section.module.less';

export const shipmentVolumeOptions = [
  { id: '100-1000', messageDescriptor: messages.oneHundredTo1000Shipments },
  { id: '1000-5000', messageDescriptor: messages.oneThousandTo5000Shipments },
  { id: '5000-10000', messageDescriptor: messages.fiveThousandTo10000Shipments },
  { id: '10000-100000', messageDescriptor: messages.tenThousandTo100000Shipments },
  { id: 'moreThan100000', messageDescriptor: messages.moreThan100000Shipments },
  { id: 'unsure', messageDescriptor: messages.unsureShipments },
];

export const timelineOptions = [
  { id: 'immediately', messageDescriptor: messages.immediately },
  { id: '1month', messageDescriptor: messages.oneMonth },
  { id: '1-3months', messageDescriptor: messages.oneToThreeMonths },
  { id: '3-6months', messageDescriptor: messages.threeToSixMonths },
  { id: '6+months', messageDescriptor: messages.sixPlusMonths },
  { id: 'unsure', messageDescriptor: messages.unsureTimeline },
];

/**
 * @param {object} props
 * @param {string} props.shipmentVolume - The selected shipment volume value
 * @param {string} props.timeline - The selected timeline value
 * @param {(id: string) => void} props.onShipmentVolumeChange - Handler for shipment volume changes
 * @param {(id: string) => void} props.onTimelineChange - Handler for timeline changes
 * @param {boolean} props.isComplete
 * @param {boolean} props.isEditing
 * @param {boolean} props.isDisabled
 * @param {boolean} props.isContinueDisabled
 * @param {() => void} props.onContinue
 * @param {() => void} props.onEdit
 * @returns {JSX.Element} The shipment volume and timeline section component
 */
export const ShipmentVolumeTimelineSection = ({
  shipmentVolume,
  timeline,
  onShipmentVolumeChange,
  onTimelineChange,
  isComplete,
  isEditing,
  isDisabled,
  isContinueDisabled,
  onContinue,
  onEdit,
}) => {
  return (
    <FbsApplicationDrawerSection
      headerMessageDescriptor={messages.shipmentVolumeTitle}
      isComplete={isComplete}
      isContinueDisabled={isContinueDisabled}
      isDisabled={isDisabled}
      isEditing={isEditing}
      onContinue={onContinue}
      onEdit={onEdit}
    >
      <FlexColumn gap={Gap.Space24}>
        <FlexColumn gap={Gap.Space16}>
          <Text textType={TextType.H4} value={messages.estimatedMonthlyVolumeDescription} />
          <RadioCheckList className={styles['radio-options-grid']}>
            {shipmentVolumeOptions.map((option) => (
              <RadioCheckListItem
                key={option.id}
                className={styles['radio-button']}
                isChecked={shipmentVolume === option.id}
                isDisabled={isComplete ? !isEditing : null}
                name='shipmentVolume'
                onChecked={() => onShipmentVolumeChange(option.id)}
                value={option.id}
              >
                <FormattedMessage {...option.messageDescriptor} />
              </RadioCheckListItem>
            ))}
          </RadioCheckList>
        </FlexColumn>
        <Divider className={styles.divider} />
        <FlexColumn gap={Gap.Space16}>
          <Text textType={TextType.H4} value={messages.targetTimelineDescription} />
          <RadioCheckList className={styles['radio-options-grid']}>
            {timelineOptions.map((option) => (
              <RadioCheckListItem
                key={option.id}
                className={styles['radio-button']}
                isChecked={timeline === option.id}
                isDisabled={isComplete ? !isEditing : null}
                name='timeline'
                onChecked={() => onTimelineChange(option.id)}
                value={option.id}
              >
                <FormattedMessage {...option.messageDescriptor} />
              </RadioCheckListItem>
            ))}
          </RadioCheckList>
        </FlexColumn>
      </FlexColumn>
    </FbsApplicationDrawerSection>
  );
};
